var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Move } from "@itsy/animate/move";
import { i18n } from "@lingui/core";
import { html, LitElement, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import "../icon/icon.js";
import { activateI18n, detectLocale } from "../i18n.js";
import { messages as daMessages } from "../modal/locales/da/messages.mjs";
import { messages as enMessages } from "../modal/locales/en/messages.mjs";
import { messages as fiMessages } from "../modal/locales/fi/messages.mjs";
import { messages as nbMessages } from "../modal/locales/nb/messages.mjs";
import { messages as svMessages } from "../modal/locales/sv/messages.mjs";
import { CanCloseMixin } from "../modal/util.js";
import { reset } from "../styles.js";
import { styles } from "./styles.js";
/**
 * The header section of a modal, typically where you place the title and a close button.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/modal/frameworks/elements)
 *
 * @parent w-modal
 *
 * @slot top - Customize the title bar, for example to have a header image that reaches the modal's edges. See the With Image story for an example.
 *
 * @csspart header - the root element inside the component.
 * @csspart top - the container for the image (`top` slot).
 * @csspart back - the back button, if visible.
 * @csspart title - the title element.
 * @csspart close - the close button, if visible.
 *
 * @cssprop --w-c-modal-header-font-size
 * @cssprop --w-c-modal-header-line-height
 * @cssprop --w-c-modal-header-margin-bottom
 */
export class WarpModalHeader extends CanCloseMixin(LitElement) {
    static { this.styles = [reset, styles]; }
    constructor() {
        super();
        /**
         * Whether the modal header should have a back button
         */
        this.back = false;
        /**
         * Lets you hide the close button in the header
         */
        this.noClose = false;
        this._hasTopContent = false;
        activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
    }
    async willUpdate(changedProperties) {
        if (changedProperties.has("back")) {
            if (!this.titleEl)
                return;
            const move = new Move(this.titleEl);
            move.when(async () => {
                await this.updateComplete;
            });
        }
    }
    emitBack() {
        this.dispatchEvent(new CustomEvent("backClicked", { bubbles: true, composed: true }));
    }
    handleTopSlotChange(slotEvent) {
        const topContent = slotEvent.target.assignedElements({
            flatten: true,
        });
        this._hasTopContent = !!topContent.length;
    }
    render() {
        const showBackButton = this.back && !this._hasTopContent; // Not showing back button when there is a top image
        const showCloseButton = !this.noClose;
        return html `
			<div
				part="header"
				?show-top=${this._hasTopContent}
				?show-back=${showBackButton}
				?show-close=${showCloseButton}
			>
				<slot
					part="top"
					name="top"
					@slotchange=${this.handleTopSlotChange}
				></slot>

				${showBackButton
            ? html `<w-button
							type="button"
							part="back"
							title=""
							aria-label="${i18n._({
                id: "modal.aria.back",
                message: "Back",
                comment: "Aria label for the back button in modal",
            })}"
							variant="pill"
							icon-only
							@click="${this.emitBack}"
						>
							<w-icon
								name="ArrowLeft"
								size="small"
								locale="${detectLocale()}"
							></w-icon>
						</w-button>`
            : nothing}

				<p part="title">${this.title}</p>

				${showCloseButton
            ? html `<w-button
							type="button"
							part="close"
							title=""
							aria-label="${i18n._({
                id: "modal.aria.close",
                message: "Close",
                comment: "Aria label for the close button in modal",
            })}"
							variant="pill"
							icon-only
							@click="${this.close}"
						>
							<w-icon
								name="Close"
								size="small"
								locale="${detectLocale()}"
								style="height: 16px;"
								class="flex"
							></w-icon>
						</w-button>`
            : nothing}
			</div>
		`;
    }
}
__decorate([
    property({ type: String })
], WarpModalHeader.prototype, "title", void 0);
__decorate([
    property({ type: Boolean })
], WarpModalHeader.prototype, "back", void 0);
__decorate([
    property({ type: Boolean, attribute: "no-close" })
], WarpModalHeader.prototype, "noClose", void 0);
__decorate([
    state()
], WarpModalHeader.prototype, "_hasTopContent", void 0);
__decorate([
    query('[part="title"]')
], WarpModalHeader.prototype, "titleEl", void 0);
/** @deprecated Exported for backwards compatibility. Use WarpModalHeader. */
export const ModalHeader = WarpModalHeader;
if (!customElements.get("w-modal-header")) {
    customElements.define("w-modal-header", WarpModalHeader);
}
//# sourceMappingURL=modal-header.js.map